import { loadRecords, replaceRecordCache, type RequestRecord } from "./records.ts";
import { loadPieces, type Piece } from "./shelf.ts";
import { transactAll } from "./store.ts";

/**
 * The save data as a file: the shelf's pieces and the ledger's records, to be carried to
 * another browser (or kept) and read back in place of what is there.
 */
export interface SaveData {
  game: typeof GAME;
  version: typeof VERSION;
  /** When it was written (ISO 8601). */
  saved: string;
  shelf: Piece[];
  records: Record<string, RequestRecord>;
}

const GAME = "kneading-needed", VERSION = 1;

// ---------- Binary parts as text ----------

const ARRAYS = { Float32Array, Uint32Array, Uint16Array };
type ArrayName = keyof typeof ARRAYS;

const base64 = (blob: Blob) =>
  new Promise<string>((done, fail) => {
    const reader = new FileReader();
    reader.onload = () => {
      const url = reader.result as string;
      done(url.slice(url.indexOf(",") + 1));
    };
    reader.onerror = () => fail(reader.error);
    reader.readAsDataURL(blob);
  });

const bytes = (data: string) => Uint8Array.from(atob(data), (c) => c.charCodeAt(0));

/** Typed arrays and blobs (the shapes and the photos) as base64, marked with what they were. */
async function encode(value: unknown): Promise<unknown> {
  if (value instanceof Blob) return { $blob: value.type, data: await base64(value) };
  for (const [name, Type] of Object.entries(ARRAYS)) {
    if (value instanceof Type) {
      // Never over shared memory: these come out of the store.
      const view = value as ArrayBufferView<ArrayBuffer>;
      return { $array: name, data: await base64(new Blob([view])) };
    }
  }
  if (Array.isArray(value)) return await Promise.all(value.map(encode));
  if (value && typeof value === "object") {
    const entries = Object.entries(value).map(async ([k, v]) => [k, await encode(v)]);
    return Object.fromEntries(await Promise.all(entries));
  }
  return value;
}

/** Back from `encode`. */
function decode(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(decode);
  if (!value || typeof value !== "object") return value;
  const o = value as Record<string, unknown>;
  if (typeof o.$array === "string" && o.$array in ARRAYS && typeof o.data === "string") {
    return new ARRAYS[o.$array as ArrayName](bytes(o.data).buffer);
  }
  if (typeof o.$blob === "string" && typeof o.data === "string") {
    return new Blob([bytes(o.data)], { type: o.$blob });
  }
  return Object.fromEntries(Object.entries(o).map(([k, v]) => [k, decode(v)]));
}

// ---------- Save data ----------

/** Everything on the shelf and in the ledger, as a JSON file. */
export async function saveFile(): Promise<Blob> {
  const [pieces, records] = await Promise.all([loadPieces(), loadRecords()]);
  const data: SaveData = {
    game: GAME,
    version: VERSION,
    saved: new Date().toISOString(),
    // The store gives each piece its id again when read back.
    shelf: pieces.map(({ id: _, ...piece }) => piece),
    records: Object.fromEntries(records),
  };
  return new Blob([JSON.stringify(await encode(data))], { type: "application/json" });
}

/** Reads a file written by `saveFile`; throws if it is not one. */
export async function readSave(file: File): Promise<SaveData> {
  const data = decode(JSON.parse(await file.text())) as SaveData;
  const pieceOk = (p: Piece) =>
    p?.positions instanceof Float32Array && p.index instanceof Uint32Array &&
    p.photo instanceof Blob && typeof p.request?.title === "string";
  const recordOk = (r: RequestRecord) =>
    !!r && Number.isInteger(r.deliveries) && r.deliveries > 0 &&
    Number.isFinite(r.bestTotal) && r.bestTotal >= 0 &&
    Number.isInteger(r.bestStrokes) && r.bestStrokes >= 0 &&
    ["D", "C", "B", "A", "S", "SS"].includes(r.bestRank) &&
    typeof r.wishMet === "boolean" &&
    (r.mastered === undefined || typeof r.mastered === "boolean") &&
    (r.topological === undefined || typeof r.topological === "boolean") &&
    (r.sunglassesIntro === undefined || typeof r.sunglassesIntro === "boolean");
  if (
    data?.game !== GAME || data.version !== VERSION || !Array.isArray(data.shelf) ||
    !data.shelf.every(pieceOk) || !data.records || typeof data.records !== "object" ||
    Array.isArray(data.records) || !Object.values(data.records).every(recordOk)
  ) {
    throw new Error("not the save data of this game");
  }
  return data;
}

/** Puts the save data's shelf and ledger in place of the ones kept, all at once. */
export async function restoreSave(data: SaveData): Promise<void> {
  await transactAll(["shelf", "records"], "readwrite", (tx) => {
    const shelf = tx.objectStore("shelf"), records = tx.objectStore("records");
    shelf.clear();
    records.clear();
    for (const piece of data.shelf) shelf.add(piece);
    for (const [id, record] of Object.entries(data.records)) records.put(record, id);
  });
  replaceRecordCache(new Map(Object.entries(data.records)));
}
